(() => {
  const parseIntSafe = (v, fallback) => {
    const n = Number(v);
    if (!Number.isFinite(n) || n <= 0) return fallback;
    return Math.floor(n);
  };

  const escapeRegExp = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const JR_SERVERLESS_BASE = "https://bero-omega.vercel.app";
  const JR_UPVOTE_URL = `${JR_SERVERLESS_BASE}/api/junip-upvote`;
  const JR_DOWNVOTE_URL = `${JR_SERVERLESS_BASE}/api/junip-downvote`;

  const SVG_SEARCH = `<svg width="14" height="14" xmlns="http://www.w3.org/2000/svg"><path d="M6 .5a5.5 5.5 0 0 1 4.383 8.823l2.897 2.897a.75.75 0 1 1-1.06 1.06l-2.897-2.897A5.5 5.5 0 1 1 6 .5zM6 2a4 4 0 1 0 0 8 4 4 0 0 0 0-8z" fill="#000" fill-rule="evenodd"/></svg>`;
  const SVG_CLEAR = `<svg width="12" height="12" xmlns="http://www.w3.org/2000/svg"><path d="m6 4.746 4.408-4.47A.97.97 0 0 1 11.7.22c.343.288.395.76.14 1.102l-.078.09L7.236 6l4.526 4.589c.312.316.314.79.025 1.109l-.087.083a.975.975 0 0 1-1.203.022l-.09-.08L6 7.254l-4.408 4.47a.972.972 0 0 1-1.195.129L.3 11.78a.797.797 0 0 1-.14-1.102l.078-.09L4.763 6 .238 1.411A.795.795 0 0 1 .213.302L.3.22A.975.975 0 0 1 1.503.197l.09.08L6 4.745l4.408-4.47L6 4.746z" fill="#000" fill-rule="nonzero"/></svg>`;

  class JunipReviews extends HTMLElement {
    connectedCallback() {
      if (this._mounted) return;
      this._mounted = true;

      this._els = {
        toggle: this.querySelector("[data-jr-toggle]"),
        controlsDesktop: this.querySelector("[data-jr-controls]"),
        controlsMobile: this.querySelector("[data-jr-controls-mobile]"),
        searchForms: Array.from(this.querySelectorAll("[data-jr-search-form]")),
        searchInputs: Array.from(this.querySelectorAll("[data-jr-search]")),
        searchSubmitButtons: Array.from(
          this.querySelectorAll("[data-jr-search-submit]")
        ),
        sortSelects: Array.from(this.querySelectorAll("[data-jr-sort]")),
        ratingSelects: Array.from(this.querySelectorAll("[data-jr-rating]")),
        sortLabels: Array.from(this.querySelectorAll("[data-jr-sort-label]")),
        ratingLabels: Array.from(
          this.querySelectorAll("[data-jr-rating-label]")
        ),
        more: this.querySelector("[data-jr-more]"),
        empty: this.querySelector("[data-jr-empty]"),
        list: this.querySelector("[data-jr-list]"),
        reviews: Array.from(this.querySelectorAll("[data-jr-review]")),
      };

      const sort = this._els.sortSelects[0]
        ? this._els.sortSelects[0].value
        : "latest";
      const rating = this._els.ratingSelects[0]
        ? this._els.ratingSelects[0].value
        : "all";

      this._state = {
        query: "",
        sort,
        rating,
        visibleCount: this._getInitialCount(),
        didSearch: false,
        mobileOpen: false,
      };

      this._ensureSearchButtonsHaveIcons();
      this._ensureEmptyClearButton();

      this._syncSelectGroup(
        this._els.sortSelects,
        this._els.sortLabels,
        "Sort By"
      );
      this._syncSelectGroup(
        this._els.ratingSelects,
        this._els.ratingLabels,
        "Rating"
      );

      this._bind();
      this._apply(true);
      this._syncSearchIconState();
    }

    _getInitialCount() {
      return parseIntSafe(this.getAttribute("data-jr-page-size"), 5);
    }

    _getMoreStep() {
      return parseIntSafe(this.getAttribute("data-jr-more-step"), 10);
    }

    _setHidden(el, isHidden) {
      if (!el) return;
      el.classList.toggle("hidden", Boolean(isHidden));
    }

    _ensureSearchButtonsHaveIcons() {
      this._els.searchSubmitButtons.forEach((btn) => {
        if (!btn) return;

        let searchSpan = btn.querySelector("[data-jr-icon-search]");
        let clearSpan = btn.querySelector("[data-jr-icon-clear]");

        if (!searchSpan) {
          searchSpan = document.createElement("span");
          searchSpan.setAttribute("data-jr-icon-search", "");
          searchSpan.innerHTML = SVG_SEARCH;
        }

        if (!clearSpan) {
          clearSpan = document.createElement("span");
          clearSpan.setAttribute("data-jr-icon-clear", "");
          clearSpan.classList.add("hidden", "mr-2");
          clearSpan.innerHTML = SVG_CLEAR.replace(
            'width="12"',
            'width="8"'
          ).replace('height="12"', 'height="8"');
        } else {
          const svg = clearSpan.querySelector("svg");
          if (svg) {
            svg.setAttribute("width", "8");
            svg.setAttribute("height", "8");
          }
          clearSpan.classList.add("mr-2");
        }

        btn.innerHTML = "";
        btn.appendChild(clearSpan);
        btn.appendChild(searchSpan);
      });
    }

    _ensureEmptyClearButton() {
      const host = this._els.empty;
      if (!host) return;
      if (host.querySelector("[data-jr-clear-filters]")) return;

      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "button button--primary-ghosted mt-3";
      btn.textContent = "Clear Filters";
      btn.setAttribute("data-jr-clear-filters", "");
      host.appendChild(btn);

      btn.addEventListener("click", () => {
        this._resetAll();
      });
    }

    _resetAll() {
      this._state.query = "";
      this._state.didSearch = false;
      this._state.sort = "latest";
      this._state.rating = "all";
      this._state.visibleCount = this._getInitialCount();

      this._els.searchInputs.forEach((i) => {
        if (i) i.value = "";
      });

      this._els.sortSelects.forEach((s) => {
        if (s) s.value = "latest";
      });
      this._els.ratingSelects.forEach((s) => {
        if (s) s.value = "all";
      });

      this._syncSelectGroup(
        this._els.sortSelects,
        this._els.sortLabels,
        "Sort By"
      );
      this._syncSelectGroup(
        this._els.ratingSelects,
        this._els.ratingLabels,
        "Rating"
      );

      this._syncSearchIconState();
      this._apply(false);
    }

    _syncSearchIconState() {
      const hasQuery = Boolean((this._state.query || "").trim());

      this._els.searchSubmitButtons.forEach((btn) => {
        if (!btn) return;
        const searchEl = btn.querySelector("[data-jr-icon-search]");
        const clearEl = btn.querySelector("[data-jr-icon-clear]");
        if (!searchEl || !clearEl) return;

        searchEl.classList.remove("hidden");

        clearEl.classList.toggle("hidden", !hasQuery);

        btn.setAttribute("aria-label", "Search");
      });
    }

    _bind() {
      const { toggle, controlsMobile, more } = this._els;

      if (toggle && controlsMobile) {
        toggle.addEventListener("click", () => {
          this._state.mobileOpen = !this._state.mobileOpen;
          this._setHidden(controlsMobile, !this._state.mobileOpen);
          toggle.setAttribute(
            "aria-expanded",
            this._state.mobileOpen ? "true" : "false"
          );
        });
      }

      this._els.searchInputs.forEach((input) => {
        if (!input) return;
        input.addEventListener("input", () => {
          const any = this._els.searchInputs.some((i) =>
            (i?.value || "").trim()
          );
          this._state.query = any ? (input.value || "").trim() : "";
          if (!this._state.query) this._state.didSearch = false;
          this._syncSearchIconState();
        });
      });

      this._els.searchForms.forEach((form, idx) => {
        form.addEventListener("submit", (e) => {
          e.preventDefault();
          const input =
            this._els.searchInputs[idx] || this._els.searchInputs[0];
          const q = input ? (input.value || "").trim() : "";

          this._state.query = q;

          this._els.searchInputs.forEach((i) => {
            if (i && i !== input) i.value = this._state.query;
          });

          this._state.visibleCount = this._getInitialCount();
          this._state.didSearch = Boolean(this._state.query);

          this._syncSearchIconState();
          this._apply(false);
        });
      });

      this.addEventListener("click", (e) => {
        const clear =
          e.target && e.target.closest
            ? e.target.closest("[data-jr-icon-clear]")
            : null;

        if (!clear || !this.contains(clear)) return;

        if (clear.classList.contains("hidden")) return;

        e.preventDefault();
        e.stopPropagation();

        this._state.query = "";
        this._state.didSearch = false;
        this._state.visibleCount = this._getInitialCount();

        this._els.searchInputs.forEach((i) => {
          if (i) i.value = "";
        });

        this._syncSearchIconState();
        this._apply(false);
      });

      this._els.sortSelects.forEach((sel) => {
        sel.addEventListener("change", () => {
          this._state.sort = sel.value || "latest";
          this._els.sortSelects.forEach((s) => {
            if (s !== sel) s.value = this._state.sort;
          });
          this._syncSelectGroup(
            this._els.sortSelects,
            this._els.sortLabels,
            "Sort By"
          );
          this._state.visibleCount = this._getInitialCount();
          this._apply(false);
        });
      });

      this._els.ratingSelects.forEach((sel) => {
        sel.addEventListener("change", () => {
          this._state.rating = sel.value || "all";
          this._els.ratingSelects.forEach((s) => {
            if (s !== sel) s.value = this._state.rating;
          });
          this._syncSelectGroup(
            this._els.ratingSelects,
            this._els.ratingLabels,
            "Rating"
          );
          this._state.visibleCount = this._getInitialCount();
          this._apply(false);
        });
      });

      if (more) {
        more.addEventListener("click", () => {
          this._state.visibleCount += this._getMoreStep();
          this._apply(false);
        });
      }

      // Voting (unchanged)
      this._els.reviews.forEach((article) => {
        const reviewId = article.getAttribute("data-review-id") || "";
        if (!reviewId) return;

        const mobileRow = article.querySelector(".lg\\:hidden");
        const desktopRow = article.querySelector(".lg\\:grid");

        const mobileBtns = mobileRow
          ? Array.from(mobileRow.querySelectorAll("button[type='button']"))
          : [];
        const desktopBtns = desktopRow
          ? Array.from(desktopRow.querySelectorAll("button[type='button']"))
          : [];

        const downBtns = [];
        const upBtns = [];

        if (mobileBtns.length >= 2) {
          downBtns.push(mobileBtns[0]);
          upBtns.push(mobileBtns[1]);
        }
        if (desktopBtns.length >= 2) {
          downBtns.push(desktopBtns[0]);
          upBtns.push(desktopBtns[1]);
        }

        const setDisabled = (btns, disabled) => {
          btns.forEach((b) => {
            if (!b) return;
            b.disabled = Boolean(disabled);
            b.classList.toggle("opacity-60", Boolean(disabled));
            b.classList.toggle("pointer-events-none", Boolean(disabled));
          });
        };

        const setCountText = (btn, n) => {
          const span = btn ? btn.querySelector("[data-jr-count]") : null;
          if (!span) return;
          span.textContent = String(n);
        };

        const callVote = async (direction) => {
          const url = direction === "up" ? JR_UPVOTE_URL : JR_DOWNVOTE_URL;

          const res = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: reviewId }),
          });

          const json = await res.json().catch(() => ({}));
          if (!res.ok || !json?.ok) {
            const msg = json?.error || `Vote failed (${res.status})`;
            throw new Error(msg);
          }
          return json;
        };

        downBtns.forEach((btn) => {
          btn.addEventListener("click", async () => {
            setDisabled(downBtns, true);
            setDisabled(upBtns, true);
            try {
              const json = await callVote("down");
              if (typeof json.down_vote_count === "number") {
                downBtns.forEach((b) => setCountText(b, json.down_vote_count));
              }
              if (typeof json.up_vote_count === "number") {
                upBtns.forEach((b) => setCountText(b, json.up_vote_count));
              }
            } catch (e) {
              setDisabled(downBtns, false);
              setDisabled(upBtns, false);
              return;
            }
            setDisabled(downBtns, false);
            setDisabled(upBtns, false);
          });
        });

        upBtns.forEach((btn) => {
          btn.addEventListener("click", async () => {
            setDisabled(downBtns, true);
            setDisabled(upBtns, true);
            try {
              const json = await callVote("up");
              if (typeof json.up_vote_count === "number") {
                upBtns.forEach((b) => setCountText(b, json.up_vote_count));
              }
              if (typeof json.down_vote_count === "number") {
                downBtns.forEach((b) => setCountText(b, json.down_vote_count));
              }
            } catch (e) {
              setDisabled(downBtns, false);
              setDisabled(upBtns, false);
              return;
            }
            setDisabled(downBtns, false);
            setDisabled(upBtns, false);
          });
        });
      });

      const clearBtn = this.querySelector("[data-jr-clear-filters]");
      if (clearBtn) {
        clearBtn.addEventListener("click", () => this._resetAll());
      }
    }

    _syncSelectGroup(selects, labels, prefix) {
      const sel = selects[0];
      if (!sel) return;
      const opt = sel.options[sel.selectedIndex];
      const txt = opt ? opt.textContent : "";
      labels.forEach((l) => {
        if (l) l.textContent = prefix ? `${prefix}: ${txt}` : txt;
      });
    }

    _stripHighlights(reviewEl) {
      reviewEl.querySelectorAll("[data-review-body]").forEach((body) => {
        const marks = body.querySelectorAll("mark.jr-hl");
        marks.forEach((m) =>
          m.replaceWith(document.createTextNode(m.textContent || ""))
        );
        body.normalize();
      });
    }

    _highlightAndMatch(reviewEl, keyword) {
      if (!keyword) return true;
      const k = keyword.trim();
      if (!k) return true;

      let matched = false;
      const re = new RegExp(escapeRegExp(k), "ig");

      reviewEl.querySelectorAll("[data-review-body]").forEach((body) => {
        const raw = body.textContent || "";
        if (!raw) return;

        const has = re.test(raw);
        re.lastIndex = 0;
        if (!has) return;

        matched = true;

        const frag = document.createDocumentFragment();
        let lastIndex = 0;

        raw.replace(re, (match, offset) => {
          const before = raw.slice(lastIndex, offset);
          if (before) frag.appendChild(document.createTextNode(before));

          const mark = document.createElement("mark");
          mark.className = "jr-hl";
          mark.textContent = match;
          frag.appendChild(mark);

          lastIndex = offset + match.length;
          return match;
        });

        const after = raw.slice(lastIndex);
        if (after) frag.appendChild(document.createTextNode(after));

        body.textContent = "";
        body.appendChild(frag);
      });

      return matched;
    }

    _numAttr(el, name) {
      const v = Number(el.getAttribute(name) || "0");
      return Number.isFinite(v) ? v : 0;
    }

    _sort(arr, key) {
      const k = key || "latest";

      if (k === "latest")
        return arr.sort(
          (a, b) =>
            this._numAttr(b, "data-review-created") -
            this._numAttr(a, "data-review-created")
        );
      if (k === "oldest")
        return arr.sort(
          (a, b) =>
            this._numAttr(a, "data-review-created") -
            this._numAttr(b, "data-review-created")
        );
      if (k === "highest")
        return arr.sort(
          (a, b) =>
            this._numAttr(b, "data-review-rating") -
            this._numAttr(a, "data-review-rating")
        );
      if (k === "lowest")
        return arr.sort(
          (a, b) =>
            this._numAttr(a, "data-review-rating") -
            this._numAttr(b, "data-review-rating")
        );
      if (k === "helpful")
        return arr.sort(
          (a, b) =>
            this._numAttr(b, "data-review-up") -
            this._numAttr(a, "data-review-up")
        );

      return arr;
    }

    _apply(isInitial) {
      const { reviews, empty, more, list } = this._els;

      if (isInitial) this._state.visibleCount = this._getInitialCount();

      reviews.forEach((r) => {
        this._stripHighlights(r);
        this._setHidden(r, true);
      });

      // 1) FILTER by rating first
      let filtered = reviews.filter((r) => {
        if (this._state.rating !== "all") {
          const rr = this._numAttr(r, "data-review-rating");
          if (rr !== Number(this._state.rating)) return false;
        }
        return true;
      });

      // 2) SORT
      filtered = this._sort(filtered.slice(), this._state.sort);

      // 3) SEARCH within the already-filtered set
      if (this._state.didSearch && this._state.query) {
        filtered = filtered.filter((r) =>
          this._highlightAndMatch(r, this._state.query)
        );
      }

      const visible = filtered.slice(0, this._state.visibleCount);
      visible.forEach((r) => this._setHidden(r, false));

      // Re-order DOM to match sorted order
      if (list) {
        const frag = document.createDocumentFragment();
        filtered.forEach((r) => frag.appendChild(r));
        list.appendChild(frag);
      }

      const noMatches = filtered.length === 0;
      const shouldShowEmptyForSearch =
        noMatches &&
        this._state.didSearch &&
        Boolean((this._state.query || "").trim());

      if (empty) this._setHidden(empty, !shouldShowEmptyForSearch);

      if (more) {
        const hasMore = filtered.length > this._state.visibleCount;
        more.style.display = hasMore ? "inline-flex" : "none";
      }
    }
  }

  if (!customElements.get("junip-reviews"))
    customElements.define("junip-reviews", JunipReviews);
})();
